import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Grid, Fade } from "@mui/material";
import {
  ConnectComponentsProvider,
  ConnectNotificationBanner,
} from "@stripe/react-connect-js";
import EMPLoader from "../../shared/components/EMPLoader/EMPLoader";
import EMPBanner from "../../shared/components/EMPBanner/EMPBanner";
import { useFieldSupportStatus } from "../../shared/hooks/useFieldSupportStatus";
import StripeAccountDetails from "../../components/dealerOnboarding/OnboardingPaymentProcessorOnlineDeposits/StripeAccountDetails";

const StripeComplete = ({
  accountId,
  createdTimestamp,
  eventuallyDueRequirements,
  stripeConnectInstance,
  isReady = false,
  user,
  onEditPreferences,
  showEditPreferences = true,
  stripeUrl = "https://dashboard.stripe.com/login",
  brandName = "Lincoln",
}) => {
  const [bannerLoading, setBannerLoading] = useState(true);
  const isFieldSupport = useFieldSupportStatus(user);

  const onLoaderStart = (event) => {
    setBannerLoading(false);
    console.log(`${event.elementTagName} is visible to users`);
  };

  // Format the eventually due requirements into a readable string
  const formatRequirements = (requirements) => {
    if (!requirements?.length) return null;

    return requirements
      .map((requirement) => {
        // Remove the first segment (prefix) from dotted requirement strings
        const segments = requirement.split(".");
        return segments.slice(1).join(".");
      })
      .join(", ");
  };

  // Format timestamp to mm/dd/yyyy format
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formattedRequirements = formatRequirements(eventuallyDueRequirements);

  // Get banner configuration based on current state
  const getBannerConfig = () => {
    if (isFieldSupport) {
      if (isReady) {
        return {
          title: "The dealer has completed their Stripe setup.",
          content: "Their account is enabled.",
          severity: "success",
        };
      }
      return {
        title:
          "This dealer has already set up Stripe but may have required actions.",
        content:
          "Check the Stripe Connect Dashboard for additional information.",
        severity: "warning",
      };
    }

    if (isReady) {
      return {
        title: "",
        content: "Your Stripe account setup has been completed.",
        severity: "success",
      };
    }

    return {
      title: "",
      content:
        "Your Stripe account setup is complete, but there may be additional actions required.",
      severity: "warning",
    };
  };

  const bannerConfig = getBannerConfig();

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ mb: 8 }}>
        <EMPBanner {...bannerConfig} />
      </Box>
      <StripeAccountDetails
        mode="connected"
        accountId={accountId}
        completedDate={formatDate(createdTimestamp)}
        onEdit={onEditPreferences}
        showEditPreferences={showEditPreferences}
        stripeUrl={stripeUrl}
        brandName={brandName}
      />
      <Grid container alignItems="center" justifyContent="space-between">
        {formattedRequirements && (
          <Grid item xs={12} sx={{ mt: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Additional Requirements
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formattedRequirements}
              </Typography>
            </Box>
          </Grid>
        )}
        {stripeConnectInstance && (
          <Grid item xs={12} sx={{ mt: 3 }}>
            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
              {!isFieldSupport && bannerLoading && (
                <Fade in={true} timeout={300}>
                  <Box
                    sx={{
                      height: "170px",
                      border: "1px solid #ececec",
                      borderRadius: "8px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <EMPLoader loadingMessage="Checking for Stripe notifications" />
                  </Box>
                </Fade>
              )}
              {isFieldSupport ? (
                <Box
                  sx={{
                    px: 4,
                    py: 6,
                    border: "1px solid #B2B2B2",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.main"
                    textAlign="center"
                  >
                    Stripe Notifications appear here for authenticated dealers
                  </Typography>
                </Box>
              ) : (
                <ConnectNotificationBanner onLoaderStart={onLoaderStart} />
              )}
            </ConnectComponentsProvider>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

StripeComplete.propTypes = {
  accountId: PropTypes.string,
  createdTimestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  eventuallyDueRequirements: PropTypes.arrayOf(PropTypes.string),
  isReady: PropTypes.bool,
  stripeConnectInstance: PropTypes.object,
  user: PropTypes.object,
  onEditPreferences: PropTypes.func,
  showEditPreferences: PropTypes.bool,
  stripeUrl: PropTypes.string,
  brandName: PropTypes.string,
};

export default StripeComplete;
